<template>
  <main id="app" class="site-shell" :class="{ 'site-shell--with-track': showNowPlayingWidget }">
    <section
      class="profile"
      :class="{ 'profile--with-track': showNowPlayingWidget }"
      aria-label="Surface profile"
    >
      <h1>Surface</h1>
      <p class="tagline">You can find me here</p>

      <div class="icons" aria-label="Social links">
        <a
          class="icon-link"
          href="https://github.com/surf6ce"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="GitHub"
        >
          <img src="@/assets/github.png" alt="">
        </a>

        <div class="discord-container">
          <button
            type="button"
            class="icon-button"
            aria-label="Show Discord username"
            :aria-expanded="String(showDiscord)"
            @click="toggleDiscord"
          >
            <img src="@/assets/discord.png" alt="">
          </button>

          <transition name="fade">
            <button
              v-if="showDiscord"
              type="button"
              class="discord-dropdown"
              @click="copyDiscordUsername"
            >
              {{ discordUsername }}
            </button>
          </transition>
        </div>

        <a
          class="icon-link"
          href="https://twitter.com/surf6ce"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Twitter"
        >
          <img src="@/assets/twitter.png" alt="">
        </a>
      </div>

      <a
        v-if="showNowPlayingWidget"
        class="formula-widget"
        :class="{ 'formula-widget--loading': isTrackLoading }"
        :href="lastfmProfileUrl"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="nowPlayingLabel"
        :data-refresh-interval="supportsLiveTrack ? trackRefreshInterval : undefined"
        :data-last-checked="trackLastChecked || undefined"
      >
        <transition name="song-change" mode="out-in">
          <span class="formula-track" :key="trackKey">
            <span class="formula-artwork">
              <span
                class="formula-artwork-image"
                :style="{ backgroundImage: `url(${trackImage})` }"
                aria-hidden="true"
              ></span>
              <span class="formula-caption" aria-live="polite">
                <strong>{{ trackName }}</strong>
                <span>{{ trackSubline }}</span>
              </span>
            </span>
            <span class="formula-state">{{ trackState }}</span>
          </span>
        </transition>
      </a>
    </section>

    <transition name="rise">
      <div
        v-if="showCopiedNotification"
        class="copied-notification"
        role="status"
        aria-live="polite"
      >
        {{ copiedNotificationText }}
      </div>
    </transition>
  </main>
</template>

<script>
const DISCORD_USERNAME = '@surf6ce';
const TRACK_REFRESH_INTERVAL = 6000;

export default {
  name: 'App',
  data() {
    return {
      showDiscord: false,
      showCopiedNotification: false,
      copiedNotificationText: '',
      copiedNotificationTimer: null,
      discordUsername: DISCORD_USERNAME,
      lastfmUsername: process.env.VUE_APP_LASTFM_USERNAME || 'Just-Surface',
      lastfmApiKey: process.env.VUE_APP_LASTFM_API_KEY || '',
      currentTrack: null,
      isTrackLoading: false,
      trackFetchError: '',
      trackRefreshTimer: null,
      trackRefreshInterval: TRACK_REFRESH_INTERVAL,
      trackLastChecked: ''
    };
  },
  computed: {
    supportsLiveTrack() {
      return Boolean(this.lastfmUsername && this.lastfmApiKey);
    },
    showNowPlayingWidget() {
      return Boolean(this.supportsLiveTrack && this.currentTrack?.nowPlaying);
    },
    lastfmProfileUrl() {
      return `https://www.last.fm/user/${encodeURIComponent(this.lastfmUsername)}`;
    },
    trackName() {
      if (this.currentTrack?.name) return this.currentTrack.name;
      if (this.isTrackLoading) return 'Checking Last.fm';
      return 'Last.fm';
    },
    trackArtist() {
      if (this.currentTrack?.artist) return this.currentTrack.artist;
      if (this.trackFetchError) return 'Could not load recent track';
      return 'Recent listens';
    },
    trackSubline() {
      if (!this.currentTrack) return this.trackArtist;
      if (this.currentTrack.album) return `${this.currentTrack.artist} - ${this.currentTrack.album}`;
      return this.currentTrack.artist;
    },
    trackImage() {
      return this.currentTrack?.image || this.defaultTrackImage;
    },
    trackState() {
      if (!this.supportsLiveTrack) return 'Profile';
      if (this.isTrackLoading && !this.currentTrack) return 'Checking';
      if (this.trackFetchError && !this.currentTrack) return 'Unavailable';
      return this.currentTrack?.nowPlaying ? 'Now Playing' : 'Recently Played';
    },
    nowPlayingLabel() {
      return `${this.trackState}: ${this.trackName} by ${this.trackArtist}`;
    },
    trackKey() {
      if (!this.currentTrack) return 'empty';
      return [
        this.currentTrack.name,
        this.currentTrack.artist,
        this.currentTrack.album,
        this.currentTrack.image,
        this.currentTrack.nowPlaying
      ].join('|');
    },
    defaultTrackImage() {
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'%3E%3Crect width='96' height='96' rx='18' fill='%23101820'/%3E%3Cpath d='M60 20v38.7a10.8 10.8 0 1 1-6-9.7V29.3l-24 5.1v29.3a10.8 10.8 0 1 1-6-9.7V29.5L60 20Z' fill='%23f6f7fb'/%3E%3C/svg%3E";
    }
  },
  mounted() {
    if (!this.supportsLiveTrack) return;

    this.fetchNowPlaying();
    this.trackRefreshTimer = window.setInterval(() => {
      this.fetchNowPlaying({ silent: true });
    }, this.trackRefreshInterval);
  },
  beforeUnmount() {
    if (this.trackRefreshTimer) window.clearInterval(this.trackRefreshTimer);
    if (this.copiedNotificationTimer) window.clearTimeout(this.copiedNotificationTimer);
  },
  methods: {
    toggleDiscord() {
      this.showDiscord = !this.showDiscord;
    },
    async copyDiscordUsername() {
      try {
        if (!navigator.clipboard || !window.isSecureContext) {
          throw new Error('Clipboard unavailable');
        }

        await navigator.clipboard.writeText(this.discordUsername);
        this.showNotification('Copied!');
      } catch {
        this.showNotification(`Copy ${this.discordUsername}`);
      }
    },
    showNotification(message) {
      if (this.copiedNotificationTimer) {
        window.clearTimeout(this.copiedNotificationTimer);
      }

      this.copiedNotificationText = message;
      this.showCopiedNotification = true;
      this.copiedNotificationTimer = window.setTimeout(() => {
        this.showCopiedNotification = false;
      }, 2000);
    },
    getBestImage(images = []) {
      const preferredSizes = ['extralarge', 'large', 'medium'];

      for (const size of preferredSizes) {
        const match = images.find((image) => image.size === size && image['#text']);
        if (match) return match['#text'];
      }

      return images.find((image) => image['#text'])?.['#text'] || '';
    },
    async fetchRecentTrack() {
      const query = new URLSearchParams({
        method: 'user.getrecenttracks',
        user: this.lastfmUsername,
        api_key: this.lastfmApiKey,
        format: 'json',
        limit: '1',
        _: String(Date.now())
      });
      const response = await fetch(`https://ws.audioscrobbler.com/2.0/?${query.toString()}`, {
        cache: 'no-store'
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.error) throw new Error(data.message);

      const track = data.recenttracks?.track?.[0];
      if (!track) return null;

      return {
        name: track.name,
        artist: track.artist?.['#text'] || 'Unknown Artist',
        album: track.album?.['#text'] || '',
        image: this.getBestImage(track.image),
        nowPlaying: track['@attr']?.nowplaying === 'true'
      };
    },
    async fetchNowPlaying({ silent = false } = {}) {
      if (!this.supportsLiveTrack) return;
      if (!silent || !this.currentTrack) this.isTrackLoading = true;
      this.trackFetchError = '';

      try {
        const nextTrack = await this.fetchRecentTrack();
        if (nextTrack) this.currentTrack = nextTrack;
      } catch (error) {
        this.trackFetchError = error.message || 'Could not fetch Last.fm';
      } finally {
        this.trackLastChecked = new Date().toISOString();
        this.isTrackLoading = false;
      }
    }
  }
};
</script>

<style scoped>
@font-face {
  font-family: 'KiwiSoda';
  src: url('/public/fonts/KiwiSoda.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
}

:global(*) {
  box-sizing: border-box;
}

:global(body) {
  min-width: 320px;
  margin: 0;
  background: #000000;
  color: #ffffff;
  font-family: 'Figtree', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

:global(button),
:global(a) {
  -webkit-tap-highlight-color: transparent;
}

.site-shell {
  display: grid;
  min-height: 100vh;
  min-height: 100svh;
  place-items: center;
  padding: 24px 20px;
}

.site-shell--with-track {
  padding: clamp(48px, 8vh, 86px) 20px 24px;
}

.profile {
  display: flex;
  width: min(100%, 520px);
  flex-direction: column;
  align-items: center;
}

.profile--with-track {
  transform: translateY(clamp(22px, 5vh, 52px));
}

h1 {
  margin: 0;
  color: #ffffff;
  font-family: 'KiwiSoda', sans-serif;
  font-size: clamp(4rem, 18vw, 8.6rem);
  font-weight: 400;
  line-height: 0.82;
  text-align: center;
}

.tagline {
  margin: 1.35rem 0 0;
  color: rgba(255, 255, 255, 0.86);
  font-size: clamp(1.2rem, 4vw, 1.5rem);
  font-weight: 650;
  line-height: 1.2;
  text-align: center;
}

.icons {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 2rem;
}

.icon-link,
.icon-button {
  display: grid;
  width: 58px;
  height: 58px;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.icon-link:hover,
.icon-button:hover,
.icon-link:focus-visible,
.icon-button:focus-visible {
  border-color: rgba(255, 255, 255, 0.24);
  outline: none;
  transform: translateY(-2px);
}

.icon-link img,
.icon-button img {
  width: 46px;
  height: 46px;
  filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(180deg);
}

.discord-container {
  position: relative;
}

.discord-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  min-width: max-content;
  padding: 10px 16px;
  border: 1px solid #ffffff;
  border-radius: 6px;
  background: #000000;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 1.05rem;
  font-weight: 700;
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 2;
}

.discord-dropdown:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 3px;
}

.discord-dropdown::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  width: 10px;
  height: 10px;
  border-top: 1px solid #ffffff;
  border-left: 1px solid #ffffff;
  background: #000000;
  transform: translateX(-50%) rotate(45deg);
}

.formula-widget {
  display: flex;
  width: min(252px, calc(100vw - 48px));
  flex-direction: column;
  align-items: center;
  margin-top: clamp(2.35rem, 5.5vh, 3.6rem);
  color: #ffffff;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.formula-widget:hover,
.formula-widget:focus-visible {
  outline: none;
  transform: translateY(-2px);
}

.formula-track {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
}

.formula-artwork {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  background-color: #151515;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.72);
}

.formula-artwork-image {
  display: block;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
}

.formula-caption {
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  padding: 10px 14px 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  background: rgba(24, 24, 24, 0.78);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(12px) saturate(160%);
  color: #ffffff;
  line-height: 1.12;
  text-align: center;
}

.formula-caption strong,
.formula-caption span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.formula-caption strong {
  font-size: clamp(1rem, 5vw, 1.35rem);
  font-weight: 500;
}

.formula-caption span {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.88rem;
  font-weight: 600;
}

.formula-state {
  display: block;
  max-width: 100%;
  margin-top: 10px;
  padding: 8px 22px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  background: rgba(24, 24, 24, 0.78);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
}

.song-change-enter-active,
.song-change-leave-active {
  transition: opacity 0.32s ease, transform 0.32s ease, filter 0.32s ease;
}

.song-change-enter-from {
  opacity: 0;
  filter: blur(6px);
  transform: translateY(14px) scale(0.98);
}

.song-change-leave-to {
  opacity: 0;
  filter: blur(6px);
  transform: translateY(-10px) scale(0.98);
}

.song-change-enter-to,
.song-change-leave-from {
  opacity: 1;
  filter: blur(0);
  transform: translateY(0) scale(1);
}

.copied-notification {
  position: fixed;
  bottom: 24px;
  left: 50%;
  min-width: 132px;
  padding: 12px 18px;
  border: 1px solid #ffffff;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.86);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 750;
  text-align: center;
  transform: translateX(-50%);
  z-index: 10;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.rise-enter-active,
.rise-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.rise-enter-from,
.rise-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}

@media (max-width: 420px) {
  :global(html),
  :global(body) {
    height: 100%;
    overflow: hidden;
  }

  .site-shell {
    height: 100svh;
    min-height: 100svh;
    overflow: hidden;
    padding: max(14px, env(safe-area-inset-top)) 16px max(14px, env(safe-area-inset-bottom));
  }

  .site-shell--with-track {
    height: 100svh;
    min-height: 100svh;
    overflow: hidden;
    padding: max(18px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom));
  }

  .profile--with-track {
    transform: translateY(6px);
  }

  h1 {
    font-size: clamp(3.55rem, 16.5vw, 4.25rem);
  }

  .tagline {
    margin-top: 0.95rem;
    font-size: clamp(1.06rem, 3.7vw, 1.2rem);
  }

  .icons {
    gap: 10px;
    margin-top: 1.35rem;
  }

  .icon-link,
  .icon-button {
    width: 52px;
    height: 52px;
  }

  .icon-link img,
  .icon-button img {
    width: 42px;
    height: 42px;
  }

  .formula-widget {
    width: min(204px, calc(100vw - 56px));
    margin-top: 1.25rem;
  }

  .formula-caption {
    right: 10px;
    bottom: 10px;
    left: 10px;
    padding: 9px 12px 10px;
  }
}
</style>
